export const TYPES = [
  {
    value: "notifiable_incident",
    label: "Notifiable Incident",
    description: "Death, serious injury or illness, or a dangerous incident as defined by WHS legislation.",
  },
  {
    value: "near_miss",
    label: "Near Miss",
    description: "An unplanned event that could have caused harm but didn't.",
  },
  {
    value: "injury_illness",
    label: "Injury/Illness",
    description: "Physical or psychological harm, including fractures, burns, or mental distress.",
  },
  {
    value: "environmental",
    label: "Environmental",
    description: "Spills, emissions, contamination, or damage to ecosystems.",
  },
  {
    value: "property_damage",
    label: "Property Damage",
    description: "Damage to plant, equipment, vehicles, or infrastructure.",
  },
  {
    value: "security_incident",
    label: "Security Incident",
    description: "Theft, vandalism, suspicious behaviour, or unauthorised access.",
  },
  {
    value: "reputational",
    label: "Reputational",
    description: "Events that could harm public trust or the brand, such as media exposure or protest.",
  },
  {
    value: "psychosocial",
    label: "Psychosocial",
    description: "Bullying, harassment, stress, fatigue, or exposure to traumatic events.",
  },
  {
    value: "process_safety",
    label: "Process Safety",
    description: "Loss of containment, equipment failure, or critical system breach.",
  },
  {
    value: "contractor_incident",
    label: "Contractor Incident",
    description: "Any incident involving contractors or other third-party workers.",
  },
  {
    value: "public_safety_incident",
    label: "Public Safety Incident",
    description: "Harm or risk to non-workers, visitors, or the community.",
  },
  {
    value: "regulatory_breach",
    label: "Regulatory Breach",
    description: "Breach of WHS, EPA, Dangerous Goods or other regulatory obligations.",
  },
];

// Escalation notification rules. Trigger and channel copy lives here rather
// than in the components so the settings editor and the action menu describe a
// rule the same way. Role names come from the API, which already owns them.
export const NOTIFICATION_TRIGGERS = [
  {
    value: "manual",
    label: "Manually, from the incident menu",
    description: "Someone with Safety Admin access chooses when to send it.",
  },
  {
    value: "on_submit",
    label: "When an incident is submitted",
    description: "Sent as soon as the incident is reported.",
  },
  {
    value: "on_classify",
    label: "When an incident is classified",
    description: "Sent once Safety has set the classification and investigation type.",
  },
  {
    value: "on_approval",
    label: "When an incident is approved",
    description: "Sent after an approval decision is recorded. Requires an incident type.",
  },
  {
    value: "on_close",
    label: "When an incident is closed",
    description: "Sent once the incident is closed out.",
  },
];

export const NOTIFICATION_CHANNELS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
];

// Mirrors IncidentEscalationService::DEFAULT_RULES. Used only as a fallback
// when the rules endpoint cannot be reached, so the escalation the customer
// relies on does not vanish from the menu on a transient failure.
export const DEFAULT_MANUAL_NOTIFICATION_RULES = [
  { code: "notify_md", label: "Notify MD", channels: ["email", "sms"] },
];

export const OUTCOMES = [
  {
    value: "regulatory_notification",
    label: "Regulatory Notification",
    description: "Trigger a notification to regulators due to a serious or notifiable incident.",
  },
  {
    value: "corrective_action",
    label: "Corrective Action",
    description: "Actions required to address hazards, near misses, or process failures.",
  },
  {
    value: "medical_treatment",
    label: "Medical Treatment",
    description: "Medical attention provided as a result of injury or illness incidents.",
  },
  {
    value: "lost_time",
    label: "Lost Time",
    description: "Injuries or illnesses that prevent workers from performing normal duties.",
  },
  {
    value: "environmental_remediation",
    label: "Environmental Remediation",
    description: "Clean-up or restoration required after environmental incidents.",
  },
  {
    value: "asset_repair_or_replacement",
    label: "Asset Repair or Replacement",
    description: "Repair or replace plant, equipment, vehicles, or infrastructure.",
  },
  {
    value: "security_response",
    label: "Security Response",
    description: "Security actions triggered by theft, vandalism, or unauthorised access.",
  },
  {
    value: "reputation_management",
    label: "Reputation Management",
    description: "Actions to protect or restore public trust following reputational incidents.",
  },
  {
    value: "employee_support",
    label: "Employee Support",
    description: "Support provided after psychosocial incidents, including counselling or assistance.",
  },
  {
    value: "operational_disruption",
    label: "Operational Disruption",
    description: "Disruption to operations caused by process safety failures or major incidents.",
  },
  {
    value: "contractor_review",
    label: "Contractor Review",
    description: "Reviews or audits initiated following incidents involving contractors.",
  },
  {
    value: "community_engagement",
    label: "Community Engagement",
    description: "Engagement with the community when public safety or reputation is affected.",
  },
  {
    value: "compliance_review",
    label: "Compliance Review",
    description: "Review of compliance programs triggered by regulatory or internal breaches.",
  },
  {
    value: "insurance_claim",
    label: "Insurance Claim",
    description: "Claim lodged for financial recovery after loss or damage.",
  },
  {
    value: "investigation",
    label: "Investigation",
    description: "Formal review to determine root cause and contributing factors.",
  },
  {
    value: "training_or_communication",
    label: "Training or Communication",
    description: "Learning activities or communications issued following an incident.",
  },
];

export const STATUSES = [
  {
    value: "submitted_pending_safety_review",
    label: "Submitted - Pending Safety Review",
    description: "Initial submission received, awaiting Safety Admin classification.",
  },
  {
    value: "safety_review_completed",
    label: "Safety Review Completed",
    description: "Safety Admin has classified the incident (notification only or no investigation needed).",
  },
  {
    value: "investigation_in_progress",
    label: "Investigation In Progress",
    description: "Investigator has been assigned and investigation is underway.",
  },
  {
    value: "investigation_submitted_to_safety",
    label: "Investigation Submitted to Safety",
    description: "Investigator has submitted findings for Safety Admin review.",
  },
  {
    value: "investigation_approved",
    label: "Investigation Approved",
    description: "Safety Admin has approved the investigation findings.",
  },
  {
    value: "lessons_learned_pending_review",
    label: "Lessons Learned - Pending Review",
    description: "Lessons learned draft generated, pending Safety Admin review.",
  },
  {
    value: "distributed",
    label: "Distributed",
    description: "Lessons learned have been distributed to relevant stakeholders.",
  },
  {
    value: "closed",
    label: "Closed",
    description: "Incident fully resolved and closed.",
  },
];

export const INVESTIGATION_TYPES = [
  { value: 'notification_only', label: 'Notification Only' },
  { value: 'factual', label: 'Factual' },
  { value: 'five_why', label: '5 Why' },
  { value: 'icam', label: 'ICAM' },
];

export const CORRECTIVE_ACTION_STATUSES = [
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

export const INVESTIGATION_STATUSES = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'more_info_requested', label: 'More Info Requested' },
  { value: 'approved', label: 'Approved' },
];

export const LESSONS_LEARNED_STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  approved: 'Approved',
  distributed: 'Distributed',
};

const buildOptionValueMap = (options) => {
  return options.reduce((map, option) => {
    map.set(option.value, option.value);
    map.set(option.label, option.value);
    return map;
  }, new Map());
};

const normalizeOptionValue = (value, map) => {
  if (typeof value !== 'string' || !value) {
    return value;
  }

  if (map.has(value)) {
    return map.get(value);
  }

  const normalizedKey = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  if (map.has(normalizedKey)) {
    return map.get(normalizedKey);
  }

  return value;
};

const findOption = (value, options, map) => {
  const normalizedValue = normalizeOptionValue(value, map);
  return options.find(option => option.value === normalizedValue) || null;
};

const TYPE_VALUE_MAP = buildOptionValueMap(TYPES);
const OUTCOME_VALUE_MAP = buildOptionValueMap(OUTCOMES);
const STATUS_VALUE_MAP = buildOptionValueMap(STATUSES);

export const normalizeIncidentTypeValue = (value) =>
  normalizeOptionValue(value, TYPE_VALUE_MAP);

export const normalizeIncidentOutcomeValue = (value) =>
  normalizeOptionValue(value, OUTCOME_VALUE_MAP);

export const normalizeIncidentStatusValue = (value) =>
  normalizeOptionValue(value, STATUS_VALUE_MAP);

export const findIncidentTypeOption = (value) =>
  findOption(value, TYPES, TYPE_VALUE_MAP);

export const findIncidentOutcomeOption = (value) =>
  findOption(value, OUTCOMES, OUTCOME_VALUE_MAP);

export const findIncidentStatusOption = (value) =>
  findOption(value, STATUSES, STATUS_VALUE_MAP);
